import { useCallback, useEffect, useRef } from 'react';

import { useUser } from '@auth-v2';

import { Nil } from '@core';
import { useUiNodeTitle } from '@hooks';

import { NO_TEXT, SCREEN_PADDING_X } from '../consts';
import { type MarkerWithPosition } from '../ScrubberMarks';

import { SVGMark } from './SvgMark';
import { SwingThreadRenderer } from './SwingThreadRenderer';

const CARD_OFFSET = 40;

type ScrubberMarkProps = {
    activeUserId: string;
    left: string;
    fromFrame: number;
    swingUuid: string;
    markers: MarkerWithPosition[];
    totalFrames?: number | Nil;
    openThreads: Record<number, boolean>;
    setOpenThreads: (threads: Record<number, boolean>) => void;
};

export const ScrubberMark = ({ left, markers, activeUserId, openThreads, setOpenThreads }: ScrubberMarkProps) => {
    const { data: user } = useUser();

    const threadRefs = useRef<(HTMLDivElement | null)[]>([]);
    const markerRef = useRef<HTMLDivElement>(null);

    const anyOpen = markers.some((m) => openThreads[m.id]);

    const closeThread = useCallback(
        (id: number) => {
            setOpenThreads({
                ...openThreads,
                [id]: false,
            });
        },
        [openThreads, setOpenThreads],
    );

    const handleToggleMarkerClick = useCallback(() => {
        const nextOpen = !anyOpen;
        const updated = { ...openThreads };
        markers.forEach((mark) => {
            updated[mark.id] = nextOpen;
        });
        setOpenThreads(updated);
    }, [anyOpen, markers, openThreads, setOpenThreads]);

    useEffect(() => {
        if (!anyOpen || !markerRef.current) return;

        const markerRect = markerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        threadRefs.current.forEach((threadEl, i) => {
            if (!threadEl) return;

            const threadRect = threadEl.getBoundingClientRect();
            const startingPoint = -1 * CARD_OFFSET * threadRefs.current.length - 1;
            const nudge = i * CARD_OFFSET;

            const centeredX = markerRect.left - threadRect.width / 2 + startingPoint + nudge;
            const aboveY = markerRect.top - threadRect.height - 36;

            const min = Math.max(centeredX, SCREEN_PADDING_X);
            const max = Math.min(viewportWidth - threadRect.width - SCREEN_PADDING_X);

            threadEl.style.transform = `translate3d(${Math.min(min, max)}px, ${aboveY}px, 0)`;
        });
    }, [anyOpen, markers.length, openThreads]);

    const getTitle = useUiNodeTitle();

    return (
        <>
            <div ref={markerRef} style={{ position: 'absolute', left, top: -4 }}>
                <SVGMark onClick={handleToggleMarkerClick} isOpen={anyOpen} numberOfThreads={markers.length} />
            </div>

            {markers.map((marker, index) => (
                <SwingThreadRenderer
                    key={marker.id}
                    ref={(el) => {
                        threadRefs.current[index] = el;
                    }}
                    activePosition={marker.pPosition}
                    swingUuid={marker.swingUuid}
                    currentUserId={user!.id}
                    threadId={marker.id}
                    title={getTitle(marker.linkNodeId, NO_TEXT)}
                    nodeId={marker.linkNodeId}
                    fromFrame={marker.fromFrame}
                    visible={openThreads[marker.id] ?? false}
                    onClose={() => closeThread(marker.id)}
                    activeUserId={activeUserId}
                />
            ))}
        </>
    );
};
